import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { 
  TrendingUp, BarChart3, Search, Globe, Sparkles, Settings, LogOut, Crown, Menu, X 
} from 'lucide-react';
import { useState } from 'react';

// Infobulles pour la navigation
const NAV_TOOLTIPS = {
  portfolio: "Vue d'ensemble de votre portefeuille et ses performances clés.",
  backtest: "Testez la performance historique d'une allocation avant d'investir.",
  screening: "Recherchez et filtrez des actions selon vos critères.",
  geographic: "Visualisez la répartition géographique de vos investissements.",
  advisor: "Conseiller macro-sectoriel : régime économique, secteurs favorisés, briefings personnalisés.",
  premium: "Fonctionnalité réservée aux abonnés Premium (199€/an).",
};

const Navbar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/portfolio', label: 'Portfolio', icon: TrendingUp, tooltip: NAV_TOOLTIPS.portfolio },
    { path: '/backtest', label: 'Backtest', icon: BarChart3, tooltip: NAV_TOOLTIPS.backtest },
    { path: '/screening', label: 'Screening', icon: Search, tooltip: NAV_TOOLTIPS.screening },
    { path: '/geographic', label: 'Geographic', icon: Globe, tooltip: NAV_TOOLTIPS.geographic },
    { path: '/advisor', label: 'Advisor', icon: Sparkles, highlight: true, tooltip: NAV_TOOLTIPS.advisor },
  ];

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <nav className="glass sticky top-0 z-50 border-b border-border">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div 
            className="flex items-center gap-3 cursor-pointer" 
            onClick={() => navigate('/advisor')}
          >
            <div className="w-9 h-9 rounded-sm bg-primary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-white font-bold text-lg leading-tight font-manrope">Oracle Portfolio</h1>
              <p className="text-muted-foreground text-xs leading-tight">Unified Platform</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <TooltipProvider delayDuration={400}>
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      <Button
                        variant={isActive(item.path) ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => navigate(item.path)}
                        className={`gap-2 ${
                          isActive(item.path)
                            ? 'bg-primary text-white'
                            : item.highlight 
                              ? 'text-primary hover:bg-primary/10 hover:text-primary'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                        data-testid={`nav-${item.label.toLowerCase()}`}
                      >
                        <Icon className="w-4 h-4" />
                        {item.label}
                        {item.highlight && !isActive(item.path) && (
                          <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent 
                      side="bottom" 
                      className="max-w-[280px] bg-popover text-popover-foreground border border-border p-3"
                    >
                      {item.tooltip}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </TooltipProvider>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {user?.is_premium && (
              <Badge className="hidden sm:flex bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                <Crown className="w-3 h-3 mr-1" />
                Premium
              </Badge>
            )}
            
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/settings')}
              className="hidden md:flex"
              data-testid="nav-settings"
            >
              <Settings className="w-5 h-5" />
            </Button>
            
            {onLogout && (
              <Button 
                variant="ghost" 
                size="icon"
                onClick={onLogout}
                className="hidden md:flex"
                data-testid="nav-logout"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <div className="flex flex-col gap-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.path}
                    variant={isActive(item.path) ? 'default' : 'ghost'}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`justify-start gap-3 ${
                      isActive(item.path) ? 'bg-primary text-white' : ''
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Button>
                );
              })}
              <div className="border-t border-border my-2"></div>
              <Button
                variant="ghost"
                onClick={() => {
                  navigate('/settings');
                  setMobileMenuOpen(false);
                }}
                className="justify-start gap-3"
              >
                <Settings className="w-4 h-4" />
                Paramètres
              </Button>
              {onLogout && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    onLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="justify-start gap-3"
                >
                  <LogOut className="w-4 h-4" />
                  Déconnexion
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
